import { useState, useEffect, useCallback, Fragment } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { useLocale } from '@/contexts/LocaleContext'
import { formatDateTime } from '@/utils/formatDateTime'
import { getRecordingStatusLabel } from '@/utils/recordingStatus'
import {
  Assignment as AssignmentIcon,
  PlayArrow as PlayArrowIcon,
  VideoLibrary as VideoLibraryIcon,
  ArrowBack as ArrowBackIcon,
  Analytics as AnalyticsIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
} from '@mui/icons-material'
import { recordingService } from '@/services/recordingService'
import { resolvePlayableVideo } from '@/utils/playableVideo'
import type { CreateRecordingTaskRequest, CreateRecordingSessionRequest, UploadVideoRequest, RecordingTask, RecordingSession, Recording } from '@/services/recordingService'
import { ApiError } from '@/services/baseService'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`recording-tabpanel-${index}`}
      aria-labelledby={`recording-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: { xs: 2, md: 3 } }}>{children}</Box>}
    </div>
  )
}

export default function ParticipantRecordingManagement() {
  const { t: tRecording } = useTranslation('recording')
  const { t: tCommon } = useTranslation('common')
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const { language } = useLocale()
  const { projectId, participantId } = useParams<{ projectId: string; participantId: string }>()
  const navigate = useNavigate()

  const [activeTab, setActiveTab] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [recordingTasks, setRecordingTasks] = useState<RecordingTask[]>([])
  const [, setLoadingTasks] = useState(false)
  const [recordingSessions, setRecordingSessions] = useState<RecordingSession[]>([])
  const [loadingSessions, setLoadingSessions] = useState(false)
  const [recordings, setRecordings] = useState<Recording[]>([])
  const [loadingRecordings, setLoadingRecordings] = useState(false)
  
  const [analysisStatuses, setAnalysisStatuses] = useState<Record<number, { status: string; progress: number; jobId?: number }>>({})
  const [pollingIntervals, setPollingIntervals] = useState<Record<number, NodeJS.Timeout>>({})
  const [resultsModalOpen, setResultsModalOpen] = useState(false)
  const [selectedRecordingId, setSelectedRecordingId] = useState<number | null>(null)
  const [analysisResults, setAnalysisResults] = useState<any>(null)
  const [loadingResults, setLoadingResults] = useState(false)
  const [resultsModalVideoBlobUrl, setResultsModalVideoBlobUrl] = useState<string | null>(null)
  const [loadingResultsModalVideo, setLoadingResultsModalVideo] = useState(false)
  const [taskForm, setTaskForm] = useState({
    task_type: 'recording_simple' as 'recording_with_video' | 'recording_simple',
    reaction_video: null as File | null,
    note: '',
  })

  const [sessionForm, setSessionForm] = useState({
    session_type: 'recording_simple' as 'recording_with_video' | 'recording_simple',
    reaction_video: null as File | null,
    note: '',
  })

  const [uploadForm, setUploadForm] = useState({
    video_file: null as File | null,
    note: '',
  })

  const [existingRecordingType, setExistingRecordingType] = useState<'task' | 'session' | 'upload' | null>(null)

  const getExistingTypeLabel = (type: 'task' | 'session' | 'upload') => {
    switch (type) {
      case 'task':
        return tRecording('existingTypeTask')
      case 'session':
        return tRecording('existingTypeSession')
      case 'upload':
        return tRecording('existingTypeUpload')
    }
  }

  const getSessionTypeLabel = (sessionType: string) =>
    sessionType === 'recording_with_video'
      ? tRecording('withVideoReaction')
      : tRecording('simpleRecording')

  const getRecordingMediaLabel = (type: string) => {
    if (type === 'video') return tRecording('recordingVideo')
    if (type === 'live') return tRecording('recordingLive')
    return tRecording('recordingImage')
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
    setError(null)
    setSuccess(null)
  }

  const checkExistingRecordings = useCallback(async () => {
    if (!projectId || !participantId) return

    try {
      const participantIdNum = parseInt(participantId, 10)
      const projectIdNum = parseInt(projectId, 10)

      const [tasksData, sessionsData, recordingsData] = await Promise.all([
        recordingService.getRecordingTasks(undefined, projectIdNum, participantIdNum).catch(() => []),
        recordingService.getRecordingSessions(undefined, projectIdNum, participantIdNum).catch(() => []),
        recordingService.getRecordings(participantIdNum, projectIdNum).catch(() => []),
      ])

      const tasksArray = Array.isArray(tasksData) ? tasksData : ((tasksData as any)?.results || [])
      const sessionsArray = Array.isArray(sessionsData) ? sessionsData : ((sessionsData as any)?.results || [])
      const recordingsArray = Array.isArray(recordingsData) ? recordingsData : ((recordingsData as any)?.results || [])
      
      const uploadedForAnalysis = recordingsArray.filter((recording: Recording) => 
        recording.type === 'video' && recording.storage_url.includes('recordings/videos/')
      )

      if (tasksArray.length > 0) {
        setExistingRecordingType('task')
      } else if (sessionsArray.length > 0) {
        setExistingRecordingType('session')
      } else if (uploadedForAnalysis.length > 0) {
        setExistingRecordingType('upload')
      } else {
        setExistingRecordingType(null)
      }
    } catch (err) {
      console.error('Failed to check existing recordings:', err)
      setExistingRecordingType(null)
    }
  }, [projectId, participantId])

  const loadRecordingTasks = useCallback(async () => {
    if (!projectId || !participantId) return

    try {
      setLoadingTasks(true)
      const participantIdNum = parseInt(participantId, 10)
      const projectIdNum = parseInt(projectId, 10)
      console.log('Loading recording tasks for project_participant:', participantIdNum, 'project:', projectIdNum)
      const tasksData = await recordingService.getRecordingTasks(undefined, projectIdNum, participantIdNum)
      console.log('Received tasks data:', tasksData)
      const tasksArray = Array.isArray(tasksData) 
        ? tasksData 
        : ((tasksData as any)?.results || [])
      console.log('Setting recording tasks:', tasksArray)
      setRecordingTasks(tasksArray)
      
      const taskRecordingIds = tasksArray
        .map((t: RecordingTask) => t.recording_id)
        .filter((id: number | null | undefined): id is number => id !== null && id !== undefined)
      
      if (taskRecordingIds.length > 0) {
        await loadAnalysisStatuses(taskRecordingIds)
      }
    } catch (err) {
      console.error('Failed to load recording tasks:', err)
      setRecordingTasks([])
    } finally {
      setLoadingTasks(false)
    }
  }, [projectId, participantId])

  const loadRecordingSessions = useCallback(async () => {
    if (!projectId || !participantId) return

    try {
      setLoadingSessions(true)
      const participantIdNum = parseInt(participantId, 10)
      const projectIdNum = parseInt(projectId, 10)
      const sessionsData = await recordingService.getRecordingSessions(undefined, projectIdNum, participantIdNum)
      const sessionsArray = Array.isArray(sessionsData) 
        ? sessionsData 
        : ((sessionsData as any)?.results || [])
      setRecordingSessions(sessionsArray)
      
      const sessionRecordingIds = sessionsArray
        .map((s: RecordingSession) => s.recording)
        .filter((id: number | null): id is number => id !== null)
      
      if (sessionRecordingIds.length > 0) {
        await loadAnalysisStatuses(sessionRecordingIds)
      }
    } catch (err) {
      console.error('Failed to load recording sessions:', err)
      setRecordingSessions([])
    } finally {
      setLoadingSessions(false)
    }
  }, [projectId, participantId])

  const loadRecordings = useCallback(async () => {
    if (!projectId) return

    try {
      setLoadingRecordings(true)
      const projectIdNum = parseInt(projectId, 10)
      const recordingsData = await recordingService.getRecordings(undefined, projectIdNum)
      const recordingsArray = Array.isArray(recordingsData) 
        ? recordingsData 
        : ((recordingsData as any)?.results || [])
      const uploadedForAnalysis = recordingsArray.filter((recording: Recording) => 
        recording.type === 'video' && recording.storage_url.includes('recordings/videos/')
      )
      setRecordings(uploadedForAnalysis)
      
      if (uploadedForAnalysis.length > 0) {
        const recordingIds = uploadedForAnalysis.map((r: Recording) => r.id)
        await loadAnalysisStatuses(recordingIds)
      }
    } catch (err) {
      console.error('Failed to load recordings:', err)
      setRecordings([])
    } finally {
      setLoadingRecordings(false)
    }
  }, [projectId])

  useEffect(() => {
    const loadAll = async () => {
      await Promise.all([
        loadRecordingTasks(),
        loadRecordingSessions(),
        loadRecordings()
      ])
      await checkExistingRecordings()
    }
    loadAll()
  }, [loadRecordingTasks, loadRecordingSessions, loadRecordings, checkExistingRecordings])

  useEffect(() => {
    return () => {
      Object.values(pollingIntervals).forEach(interval => clearInterval(interval))
    }
  }, [pollingIntervals])

  const loadAnalysisStatuses = useCallback(async (recordingIds: number[]) => {
    const statusPromises = recordingIds.map(async (id) => {
      try {
        const status = await recordingService.getRecordingAnalysisStatus(id)
        return { id, status }
      } catch (err) {
        console.error(`Failed to load analysis status for recording ${id}:`, err)
        return { id, status: null }
      }
    })

    const results = await Promise.all(statusPromises)
    const newStatuses: Record<number, { status: string; progress: number; jobId?: number }> = {}
    
    results.forEach(({ id, status }) => {
      if (status && status.has_analysis) {
        newStatuses[id] = {
          status: status.status || 'pending',
          progress: status.progress || 0,
          jobId: status.job_id,
        }
      } else {
        newStatuses[id] = { status: 'none', progress: 0 }
      }
    })

    setAnalysisStatuses(prev => ({ ...prev, ...newStatuses }))
  }, [])

  const startPollingAnalysisStatus = useCallback((recordingId: number, jobId: number) => {
    if (pollingIntervals[recordingId]) {
      clearInterval(pollingIntervals[recordingId])
    }

    const interval = setInterval(async () => {
      try {
        const jobStatus = await recordingService.getAnalysisJobStatus(jobId)
        setAnalysisStatuses(prev => ({
          ...prev,
          [recordingId]: {
            status: jobStatus.status,
            progress: jobStatus.progress,
            jobId
          }
        }))

        if (jobStatus.status === 'succeeded' || jobStatus.status === 'failed') {
          clearInterval(interval)
          setPollingIntervals(prev => {
            const newIntervals = { ...prev }
            delete newIntervals[recordingId]
            return newIntervals
          })
        }
      } catch (err) {
        console.error('Failed to poll analysis status:', err)
      }
    }, 2000)

    setPollingIntervals(prev => ({ ...prev, [recordingId]: interval }))
  }, [pollingIntervals])

  const resolveRecordingStorageUrl = useCallback((recordingId: number): string | null => {
    const recording = recordings.find(r => r.id === recordingId)
    if (recording?.storage_url) return recording.storage_url
    const session = recordingSessions.find(s => s.recording === recordingId)
    if (session?.recording_data?.storage_url) return session.recording_data.storage_url
    const task = recordingTasks.find(t => t.recording_id === recordingId)
    if (task?.recording_data?.storage_url) return task.recording_data.storage_url
    return null
  }, [recordings, recordingSessions, recordingTasks])

  useEffect(() => {
    if (!resultsModalOpen || !selectedRecordingId) {
      setResultsModalVideoBlobUrl(prev => {
        if (prev) URL.revokeObjectURL(prev)
        return null
      })
      setLoadingResultsModalVideo(false)
      return
    }

    let cancelled = false
    setResultsModalVideoBlobUrl(prev => {
      if (prev) URL.revokeObjectURL(prev)
      return null
    })

    void (async () => {
      setLoadingResultsModalVideo(true)
      try {
        const rawUrl = resolveRecordingStorageUrl(selectedRecordingId)
        if (!rawUrl) return
        const token = localStorage.getItem('access_token')
        if (!token) return
        // Presigned S3 URL (or blob: URL for local storage) loaded directly into
        // the player instead of streaming through the app worker.
        const resolvedUrl = await resolvePlayableVideo(rawUrl, token)
        if (cancelled) return
        setResultsModalVideoBlobUrl(resolvedUrl)
      } catch (err) {
        console.error('Failed to load recording video for results modal:', err)
      } finally {
        if (!cancelled) setLoadingResultsModalVideo(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [resultsModalOpen, selectedRecordingId, resolveRecordingStorageUrl])

  const handleAnalyze = async (recordingId: number) => {
    try {
      setLoading(true)
      setError(null)
      
      const result = await recordingService.startAnalysis(recordingId)
      
      setAnalysisStatuses(prev => ({
        ...prev,
        [recordingId]: {
          status: result.status,
          progress: result.progress,
          jobId: result.id
        }
      }))

      if (result.status === 'pending' || result.status === 'running') {
        startPollingAnalysisStatus(recordingId, result.id)
      }
    } catch (err) {
      console.error('Failed to start analysis:', err)
      
      let errorMessage = tRecording('errors.startAnalysis')
      
      if (err instanceof ApiError) {
        const errorText = err.message || JSON.stringify(err.data || {})
        
        if (errorText.includes('6379') || errorText.includes('Redis') || errorText.includes('ConnectionError')) {
          errorMessage = tRecording('errors.redisUnavailable')
        } else if (err.data?.error) {
          errorMessage = err.data.error
        } else {
          errorMessage = err.message || errorMessage
        }
      } else if (err instanceof Error) {
        errorMessage = err.message
      }
      
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleViewResults = async (recordingId: number) => {
    try {
      setLoadingResults(true)
      setSelectedRecordingId(recordingId)
      const results = await recordingService.getAnalysisResults(recordingId)
      setAnalysisResults(results)
      setResultsModalOpen(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : tRecording('errors.loadAnalysisResults'))
    } finally {
      setLoadingResults(false)
    }
  }


  const getSessionStatusColor = (status: string): 'warning' | 'info' | 'success' | 'error' => {
    switch (status) {
      case 'created':
        return 'warning'
      case 'started':
      case 'recording':
        return 'info'
      case 'completed':
        return 'success'
      case 'cancelled':
        return 'error'
      default:
        return 'warning'
    }
  }

  const handleCreateTask = async () => {
    if (!projectId || !participantId) return

    try {
      setLoading(true)
      setError(null)
      setSuccess(null)

      const taskData: CreateRecordingTaskRequest = {
        project_participant_id: parseInt(participantId),
        task_type: taskForm.task_type,
        reaction_video: taskForm.reaction_video,
        note: taskForm.note || undefined,
      }

      await recordingService.createRecordingTask(taskData)
      setSuccess(tRecording('taskCreatedSuccess'))
      setTaskForm({
        task_type: 'recording_simple',
        reaction_video: null,
        note: '',
      })
      await loadRecordingTasks()
    } catch (err) {
      setError(err instanceof Error ? err.message : tRecording('errors.createTask'))
    } finally {
      setLoading(false)
    }
  }

  const handleCreateSession = async () => {
    if (!projectId || !participantId) return

    try {
      setLoading(true)
      setError(null)
      setSuccess(null)

      const sessionData: CreateRecordingSessionRequest = {
        project_participant_id: parseInt(participantId),
        session_type: sessionForm.session_type,
        reaction_video: sessionForm.reaction_video,
        note: sessionForm.note || undefined,
      }

      const session = await recordingService.createRecordingSession(sessionData)
      setSuccess(tRecording('sessionCreatedRedirect'))
      await loadRecordingSessions()
      
      setTimeout(() => {
        const params = new URLSearchParams({ sessionId: String(session.id) })
        if (session.note) {
          params.set('note', session.note)
        }
        navigate(`/recording-session?${params.toString()}`)
      }, 1000)
    } catch (err) {
      setError(err instanceof Error ? err.message : tRecording('errors.createSession'))
    } finally {
      setLoading(false)
    }
  }

  const handleUploadVideo = async () => {
    if (!projectId || !participantId || !uploadForm.video_file) {
      setError(tRecording('selectVideoFile'))
      return
    }

    try {
      setLoading(true)
      setError(null)
      setSuccess(null)

      const uploadData: UploadVideoRequest = {
        participant_id: parseInt(participantId),
        project_id: parseInt(projectId),
        video_file: uploadForm.video_file,
        note: uploadForm.note || undefined,
      }

      const result = await recordingService.uploadVideoForAnalysis(uploadData)
      setSuccess(tRecording('videoUploadedSuccess', { jobId: result.analysis_job_id }))
      setUploadForm({
        video_file: null,
        note: '',
      })
      await loadRecordings()
    } catch (err) {
      setError(err instanceof Error ? err.message : tRecording('errors.uploadVideo'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, flexWrap: 'wrap' }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/projects?projectId=${projectId}`)}
          size="small"
        >
          {tRecording('backToProject')}
        </Button>
        <Typography variant="h4" sx={{ fontSize: { xs: 24, md: 34 } }}>{tRecording('participantRecordingsTitle')}</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="recording management tabs"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab icon={<AssignmentIcon />} iconPosition="start" label={tRecording('tabAssignTask')} />
          <Tab icon={<PlayArrowIcon />} iconPosition="start" label={tRecording('tabStartSession')} />
          <Tab icon={<VideoLibraryIcon />} iconPosition="start" label={tRecording('tabUploadVideo')} />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          {existingRecordingType && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              {tRecording('existingRecordingWarning', { type: getExistingTypeLabel(existingRecordingType) })}
            </Alert>
          )}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {tRecording('createRecordingTask')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {tRecording('taskAssignDescription')}
              </Typography>

              <FormControl component="fieldset" sx={{ mb: 3 }} disabled={!!existingRecordingType}>
                <FormLabel component="legend">{tRecording('recordingType')}</FormLabel>
                <RadioGroup
                  value={taskForm.task_type}
                  onChange={(e) =>
                    setTaskForm((prev) => ({
                      ...prev,
                      task_type: e.target.value as 'recording_with_video' | 'recording_simple',
                    }))
                  }
                >
                  <FormControlLabel
                    value="recording_simple"
                    control={<Radio />}
                    label={tRecording('simpleRecording')}
                  />
                  <FormControlLabel
                    value="recording_with_video"
                    control={<Radio />}
                    label={tRecording('withVideoReaction')}
                  />
                </RadioGroup>
              </FormControl>

              {taskForm.task_type === 'recording_with_video' && (
                <TextField
                  fullWidth
                  type="file"
                  inputProps={{ accept: 'video/*' }}
                  onChange={(e) => {
                    const file = (e.target as HTMLInputElement).files?.[0]
                    setTaskForm((prev) => ({ ...prev, reaction_video: file || null }))
                  }}
                  sx={{ mb: 2 }}
                  disabled={!!existingRecordingType}
                />
              )}

              <TextField
                fullWidth
                label={tRecording('noteInstructionOptional')}
                multiline
                rows={4}
                value={taskForm.note}
                onChange={(e) => setTaskForm((prev) => ({ ...prev, note: e.target.value }))}
                sx={{ mb: 3 }}
                disabled={!!existingRecordingType}
              />

              <Button
                variant="contained"
                onClick={handleCreateTask}
                disabled={loading || !!existingRecordingType}
                startIcon={loading ? <CircularProgress size={20} /> : <AssignmentIcon />}
              >
                {loading ? tRecording('creating') : tRecording('createTask')}
              </Button>
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {existingRecordingType && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              {tRecording('existingRecordingWarning', { type: getExistingTypeLabel(existingRecordingType) })}
            </Alert>
          )}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {tRecording('startRecordingSession')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {tRecording('sessionStartDescription')}
              </Typography>

              <FormControl component="fieldset" sx={{ mb: 3 }} disabled={!!existingRecordingType}>
                <FormLabel component="legend">{tRecording('recordingType')}</FormLabel>
                <RadioGroup
                  value={sessionForm.session_type}
                  onChange={(e) =>
                    setSessionForm((prev) => ({
                      ...prev,
                      session_type: e.target.value as 'recording_with_video' | 'recording_simple',
                    }))
                  }
                >
                  <FormControlLabel
                    value="recording_simple"
                    control={<Radio />}
                    label={tRecording('simpleRecording')}
                  />
                  <FormControlLabel
                    value="recording_with_video"
                    control={<Radio />}
                    label={tRecording('withVideoReaction')}
                  />
                </RadioGroup>
              </FormControl>

              {sessionForm.session_type === 'recording_with_video' && (
                <TextField
                  fullWidth
                  type="file"
                  inputProps={{ accept: 'video/*' }}
                  onChange={(e) => {
                    const file = (e.target as HTMLInputElement).files?.[0]
                    setSessionForm((prev) => ({ ...prev, reaction_video: file || null }))
                  }}
                  sx={{ mb: 2 }}
                  disabled={!!existingRecordingType}
                />
              )}

              <TextField
                fullWidth
                label={tRecording('noteInstructionOptional')}
                multiline
                rows={4}
                value={sessionForm.note}
                onChange={(e) => setSessionForm((prev) => ({ ...prev, note: e.target.value }))}
                sx={{ mb: 3 }}
                disabled={!!existingRecordingType}
              />

              <Button
                variant="contained"
                onClick={handleCreateSession}
                disabled={loading || !!existingRecordingType}
                startIcon={loading ? <CircularProgress size={20} /> : <PlayArrowIcon />}
              >
                {loading ? tRecording('creating') : tRecording('startSession')}
              </Button>
            </CardContent>
          </Card>

          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PlayArrowIcon color="primary" />
                <Typography variant="h6">{tRecording('recordingSessions')}</Typography>
              </Box>
              {loadingSessions ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : recordingSessions.length > 0 ? (
                <List>
                  {recordingSessions.map((session, index) => (
                    <Fragment key={session.id}>
                      <ListItem
                        sx={{
                          flexDirection: { xs: 'column', sm: 'row' },
                          alignItems: { xs: 'flex-start', sm: 'center' },
                          gap: { xs: 1.5, sm: 0 },
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="subtitle1" component="span">
                                {getSessionTypeLabel(session.session_type)}
                              </Typography>
                              <Chip
                                label={getRecordingStatusLabel(session.status, tRecording)}
                                size="small"
                                color={getSessionStatusColor(session.status)}
                              />
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 1 }}>
                              {session.note && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  {session.note}
                                </Typography>
                              )}
                              <Typography variant="caption" color="text.secondary" component="div">
                                {tRecording('createdAt', { date: formatDateTime(session.created_at, language) })}
                              </Typography>
                              {session.created_by_name && (
                                <Typography variant="caption" color="text.secondary" component="div">
                                  {tRecording('createdBy', { name: session.created_by_name })}
                                </Typography>
                              )}
                              {session.started_at && (
                                <Typography variant="caption" color="text.secondary" component="div">
                                  {tRecording('startedAt', { date: formatDateTime(session.started_at, language) })}
                                </Typography>
                              )}
                              {session.ended_at && (
                                <Typography variant="caption" color="text.secondary" component="div">
                                  {tRecording('completedAt', { date: formatDateTime(session.ended_at, language) })}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                        {session.recording && (() => {
                          const recordingId = session.recording
                          const recording = session.recording_data
                          
                          if (!recording || (recording.type !== 'video' && recording.type !== 'live')) {
                            return null
                          }
                          
                          const analysisStatus = analysisStatuses[recordingId]
                          const currentStatus = analysisStatus?.status || 'none'
                          
                          if (currentStatus === 'succeeded') {
                            return (
                              <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<VisibilityIcon />}
                                  onClick={() => handleViewResults(recordingId)}
                                >
                                  {tRecording('viewResults')}
                                </Button>
                              </Box>
                            )
                          }
                          
                          if (currentStatus === 'running' || (currentStatus === 'pending' && (analysisStatus?.progress || 0) > 0)) {
                            return (
                              <Box sx={{ minWidth: 150 }}>
                                <LinearProgress 
                                  variant="determinate" 
                                  value={analysisStatus?.progress || 0} 
                                  sx={{ mb: 0.5 }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {analysisStatus?.progress || 0}%
                                </Typography>
                              </Box>
                            )
                          }
                          
                          return (
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<AnalyticsIcon />}
                              onClick={() => handleAnalyze(recordingId)}
                              disabled={loading}
                            >
                              {tRecording('analyze')}
                            </Button>
                          )
                        })()}
                      </ListItem>
                      {index < recordingSessions.length - 1 && <Divider />}
                    </Fragment>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                  {tRecording('noSessionsForParticipant')}
                </Typography>
              )}
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          {existingRecordingType && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              {tRecording('existingRecordingWarning', { type: getExistingTypeLabel(existingRecordingType) })}
            </Alert>
          )}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {tRecording('uploadVideoAnalysis')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {tRecording('uploadVideoDescription')}
              </Typography>

              <TextField
                fullWidth
                type="file"
                inputProps={{ accept: 'video/*' }}
                onChange={(e) => {
                  const file = (e.target as HTMLInputElement).files?.[0]
                  setUploadForm((prev) => ({ ...prev, video_file: file || null }))
                }}
                sx={{ mb: 2 }}
                disabled={!!existingRecordingType}
              />

              {uploadForm.video_file && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {tRecording('selectedFile', { name: uploadForm.video_file.name })}
                </Typography>
              )}

              <TextField
                fullWidth
                label={tRecording('noteOptional')}
                multiline
                rows={4}
                value={uploadForm.note}
                onChange={(e) => setUploadForm((prev) => ({ ...prev, note: e.target.value }))}
                sx={{ mb: 3 }}
                disabled={!!existingRecordingType}
              />

              <Button
                variant="contained"
                onClick={handleUploadVideo}
                disabled={loading || !uploadForm.video_file || !!existingRecordingType}
                startIcon={loading ? <CircularProgress size={20} /> : <VideoLibraryIcon />}
              >
                {loading ? tRecording('uploading') : tRecording('uploadAndAnalyze')}
              </Button>
            </CardContent>
          </Card>

          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <VideoLibraryIcon color="primary" />
                <Typography variant="h6">{tRecording('uploadedRecordings')}</Typography>
              </Box>
              {loadingRecordings ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : recordings.length > 0 ? (
                <List>
                  {recordings.map((recording, index) => (
                    <Fragment key={recording.id}>
                      <ListItem
                        sx={{
                          flexDirection: { xs: 'column', sm: 'row' },
                          alignItems: { xs: 'flex-start', sm: 'center' },
                          gap: { xs: 1.5, sm: 0 },
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="subtitle1" component="span">
                                {getRecordingMediaLabel(recording.type)}
                              </Typography>
                              {recording.duration_seconds && (
                                <Typography variant="caption" color="text.secondary">
                                  ({Math.round(recording.duration_seconds)}s)
                                </Typography>
                              )}
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 1 }}>
                              {recording.note && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  {recording.note}
                                </Typography>
                              )}
                              <Typography variant="caption" color="text.secondary" component="div">
                                {tRecording('createdAt', { date: formatDateTime(recording.created_at, language) })}
                              </Typography>
                              {recording.fps && (
                                <Typography variant="caption" color="text.secondary" component="div">
                                  FPS: {recording.fps}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          {(() => {
                            const analysisStatus = analysisStatuses[recording.id]
                            const currentStatus = analysisStatus?.status || 'none'
                            const progress = analysisStatus?.progress || 0
                            
                            if (currentStatus === 'succeeded') {
                              return (
                                <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<VisibilityIcon />}
                                    onClick={() => handleViewResults(recording.id)}
                                  >
                                    {tRecording('viewResults')}
                                  </Button>
                                </Box>
                              )
                            }
                            
                            if (currentStatus === 'running' || (currentStatus === 'pending' && progress > 0)) {
                              return (
                                <Box sx={{ minWidth: 150 }}>
                                  <LinearProgress 
                                    variant="determinate" 
                                    value={progress} 
                                    sx={{ mb: 0.5 }}
                                  />
                                  <Typography variant="caption" color="text.secondary">
                                    {progress}%
                                  </Typography>
                                </Box>
                              )
                            }
                            
                            return (
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<AnalyticsIcon />}
                                onClick={() => handleAnalyze(recording.id)}
                                disabled={loading}
                              >
                                {tRecording('analyze')}
                              </Button>
                            )
                          })()}
                        </Box>
                      </ListItem>
                      {index < recordings.length - 1 && <Divider />}
                    </Fragment>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                  {tRecording('noUploadedForParticipant')}
                </Typography>
              )}
            </CardContent>
          </Card>
        </TabPanel>
      </Paper>

      <Dialog
        open={resultsModalOpen}
        onClose={() => {
          setResultsModalVideoBlobUrl(prev => {
            if (prev) URL.revokeObjectURL(prev)
            return null
          })
          setResultsModalOpen(false)
        }}
        maxWidth="lg"
        fullWidth
        fullScreen={fullScreen}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">{tRecording('analysisResults')}</Typography>
            <IconButton
              onClick={() => {
                setResultsModalVideoBlobUrl(prev => {
                  if (prev) URL.revokeObjectURL(prev)
                  return null
                })
                setResultsModalOpen(false)
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingResults ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : analysisResults ? (
            <Box>
              {analysisResults.job && analysisResults.job.status !== 'succeeded' && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  {tRecording('analysisNotComplete', { status: analysisResults.job.status })}
                </Alert>
              )}
              
              {selectedRecordingId ? (
                <Box sx={{ mb: 3 }}>
                  {loadingResultsModalVideo ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : resultsModalVideoBlobUrl ? (
                    <video
                      src={resultsModalVideoBlobUrl}
                      controls
                      style={{ width: '100%', maxHeight: '400px' }}
                    />
                  ) : null}
                </Box>
              ) : null}
              
              {(() => {
                console.log('Rendering chart, analysisResults:', analysisResults)
                console.log('Frame results:', analysisResults.frame_results)
                console.log('Frame results length:', analysisResults.frame_results?.length)
                console.log('Job status:', analysisResults.job?.status)
                
                if (!analysisResults.frame_results || analysisResults.frame_results.length === 0) {
                  console.log('No frame_results, returning message')
                  return (
                    <Card sx={{ mb: 3 }}>
                      <CardContent>
                        <Typography variant="body2" color="text.secondary">
                          {tRecording('noChartData')}{' '}
                          {analysisResults.job?.status === 'succeeded'
                            ? tRecording('noChartDataSucceeded')
                            : tRecording('analysisStatusLabel', {
                                status: analysisResults.job?.status || tCommon('none'),
                              })}
                        </Typography>
                      </CardContent>
                    </Card>
                  )
                }
                
                const frameResults = analysisResults.frame_results
                console.log('Frame results count:', frameResults.length)
                
                const allEmotions = new Set<string>()
                frameResults.forEach((frame: any) => {
                  if (frame.emotions_vector) {
                    Object.keys(frame.emotions_vector).forEach(emotion => allEmotions.add(emotion))
                  }
                })
                
                console.log('All emotions:', Array.from(allEmotions))
                
                if (allEmotions.size === 0) {
                  console.log('No emotions found, returning null')
                  return (
                    <Card sx={{ mb: 3 }}>
                      <CardContent>
                        <Typography variant="body2" color="text.secondary">
                          {tRecording('noEmotionData')}
                        </Typography>
                      </CardContent>
                    </Card>
                  )
                }
                
                const emotionsArray = Array.from(allEmotions).sort()
                
                const chartData = frameResults.map((frame: any) => {
                  const dataPoint: any = {
                    timestamp_ms: frame.timestamp_ms || 0,
                  }
                  emotionsArray.forEach(emotion => {
                    dataPoint[emotion] = frame.emotions_vector?.[emotion] || 0
                  })
                  return dataPoint
                })
                
                console.log('Chart data sample:', chartData.slice(0, 3))
                console.log('Emotions array:', emotionsArray)
                
                const colors = [
                  '#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#8dd1e1',
                  '#d084d0', '#ffb347', '#87ceeb', '#dda0dd', '#f0e68c',
                  '#98d8c8', '#f7dc6f', '#bb8fce', '#85c1e2', '#f8b88b'
                ]
                
                return (
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {tRecording('emotionsOverTimeChart')}
                      </Typography>
                      <Box sx={{ width: '100%', height: '400px', mt: 2, minHeight: '400px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart
                            data={chartData}
                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="timestamp_ms"
                              label={{ value: tRecording('timeMs'), position: 'insideBottom', offset: -5 }}
                              type="number"
                              scale="linear"
                              domain={['dataMin', 'dataMax']}
                            />
                            <YAxis
                              label={{ value: tRecording('emotionValue'), angle: -90, position: 'insideLeft' }}
                              domain={[0, 1]}
                              tickFormatter={(value) => (value * 100).toFixed(0) + '%'}
                            />
                            <Tooltip
                              formatter={(value: number) => [(value * 100).toFixed(2) + '%', '']}
                              labelFormatter={(label) => tRecording('timeLabelMs', { ms: label })}
                            />
                            <Legend />
                            {emotionsArray.map((emotion, index) => (
                              <Line
                                key={emotion}
                                type="monotone"
                                dataKey={emotion}
                                stroke={colors[index % colors.length]}
                                strokeWidth={2}
                                dot={false}
                                activeDot={{ r: 4 }}
                              />
                            ))}
                          </LineChart>
                        </ResponsiveContainer>
                      </Box>
                    </CardContent>
                  </Card>
                )
              })()}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {tRecording('noResultsToDisplay')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResultsModalOpen(false)}>{tCommon('close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

